import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@os/components/ui/button';
import { Progress } from '@os/components/ui/progress';
import { Card, CardContent, CardHeader, CardTitle } from '@os/components/ui/card';
import { Play, Pause, Square, RotateCcw, Settings, Maximize, Minimize } from 'lucide-react';
import { globalVFS } from '@os/utils/storage/VFS';

interface DiskSector {
  id: number;
  type: 'free' | 'used' | 'fragmented' | 'system' | 'bad';
  fileId?: number;
  scanning?: boolean;
  moving?: boolean;
  analyzed?: boolean;
  processed?: boolean; // New: tracks if sector has been fully processed/defragmented
}

export const DiskDefrag: React.FC = () => {
  const [sectors, setSectors] = useState<DiskSector[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [currentSector, setCurrentSector] = useState(0);
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState<'analysis' | 'defrag' | 'complete' | 'idle'>('idle');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentBlock, setCurrentBlock] = useState(0);
  const [totalBlocks, setTotalBlocks] = useState(1);
  const [vfsSize, setVfsSize] = useState(0);
  const [stats, setStats] = useState({
    totalSectors: 0,
    freeSectors: 0,
    usedSectors: 0,
    fragmentedFiles: 0,
    fragmentationPercent: 0
  });

  const BLOCK_SIZE = 625; // 25x25 grid per block
  const GRID_SIZE = 25; // Always 25x25 for display
  const TOTAL_SECTORS_PER_BLOCK = GRID_SIZE * GRID_SIZE;

  // Get VFS size and calculate total blocks
  const calculateVFSBlocks = useCallback(async () => {
    try {
      const allFiles = await globalVFS.getAllFiles();
      const totalSize = allFiles.reduce((acc, file) => acc + (file.size || 0), 0);
      setVfsSize(totalSize);
      
      // Calculate blocks based on VFS size (each block represents ~1MB of data)
      const sizeInMB = totalSize / (1024 * 1024);
      const blocks = Math.max(1, Math.ceil(sizeInMB / 1)); // 1 block per MB
      setTotalBlocks(blocks);
      
      console.log(`VFS size: ${totalSize} bytes (${sizeInMB.toFixed(2)} MB), ${blocks} blocks`);
    } catch (error) {
      console.error('Failed to get VFS size:', error);
      setTotalBlocks(1); // Default to 1 block
    }
  }, []);

  // Initialize disk with random data and restore previous scan state
  const initializeDisk = useCallback(() => {
    const storageKey = 'diskDefragState';
    const savedState = localStorage.getItem(storageKey);
    const userSeed = localStorage.getItem('diskDefragSeed') || Math.random().toString();
    localStorage.setItem('diskDefragSeed', userSeed);
    
    // Use seed for consistent randomization per user
    const seededRandom = (seed: string, index: number) => {
      const hash = (seed + index.toString()).split('').reduce((a, b) => {
        a = ((a << 5) - a) + b.charCodeAt(0);
        return a & a;
      }, 0);
      return Math.abs(hash) / 2147483647;
    };

    const newSectors: DiskSector[] = [];
    let fileId = 1;
    let analyzedSectors: Set<number> = new Set();
    let processedSectors: Set<number> = new Set();
    let lastScanTime = 0;
    let savedPhase = 'idle';
    let savedCurrentSector = 0;
    let savedProgress = 0;
    let savedCurrentBlock = 0;

    // Load previous state if exists
    if (savedState) {
      const parsed = JSON.parse(savedState);
      analyzedSectors = new Set(parsed.analyzedSectors || []);
      processedSectors = new Set(parsed.processedSectors || []);
      lastScanTime = parsed.lastScanTime || 0;
      savedPhase = parsed.phase || 'idle';
      savedCurrentSector = parsed.currentSector || 0;
      savedProgress = parsed.progress || 0;
      savedCurrentBlock = parsed.currentBlock || 0;
    }

    // Generate sectors for current block only
    // Calculate degradation over time (gradual increase in fragmented sectors)
    const timeSinceLastScan = Date.now() - lastScanTime;
    const hoursSinceLastScan = timeSinceLastScan / (1000 * 60 * 60);
    const degradationRate = Math.min(hoursSinceLastScan * 0.1, 0.5); // Max 50% degradation
    
    // Use block-specific seed to ensure different patterns per block
    const blockSeed = userSeed + '_block_' + currentBlock;
    
    for (let i = 0; i < TOTAL_SECTORS_PER_BLOCK; i++) {
      const rand = seededRandom(blockSeed, i);
      const degradationRand = seededRandom(blockSeed + 'degrade', i);
      let sector: DiskSector;
      
      if (rand < 0.05) {
        // 5% system files
        sector = { id: i, type: 'system' };
      } else if (rand < 0.08) {
        // 3% bad sectors
        sector = { id: i, type: 'bad' };
      } else if (rand < 0.4) {
        // 32% free space
        sector = { id: i, type: 'free' };
      } else if (rand < 0.7 - degradationRate) {
        // Used sectors (reduced by degradation)
        sector = { id: i, type: 'used', fileId: fileId++ };
      } else {
        // Fragmented (increased by degradation and time)
        sector = { id: i, type: 'fragmented', fileId: Math.floor(seededRandom(blockSeed + 'file', i) * 50) + 1 };
      }

      // Apply previous scan state but degrade some analyzed sectors over time
      if (analyzedSectors.has(i) && degradationRand > degradationRate) {
        sector.analyzed = true;
      }
      
      // Apply processed state
      if (processedSectors.has(i)) {
        sector.processed = true;
      }
      
      newSectors.push(sector);
    }
    
    setSectors(newSectors);
    calculateStats(newSectors);
    
    // Restore saved state
    if (savedPhase !== 'idle') {
      setPhase(savedPhase as any);
      setCurrentSector(savedCurrentSector);
      setProgress(savedProgress);
      setCurrentBlock(savedCurrentBlock);
    }
  }, [currentBlock]);

  const calculateStats = (sectorArray: DiskSector[]) => {
    const freeSectors = sectorArray.filter(s => s.type === 'free').length;
    const usedSectors = sectorArray.filter(s => s.type === 'used').length;
    const fragmentedSectors = sectorArray.filter(s => s.type === 'fragmented').length;
    const fragmentedFiles = new Set(
      sectorArray.filter(s => s.type === 'fragmented').map(s => s.fileId)
    ).size;
    
    setStats({
      totalSectors: TOTAL_SECTORS_PER_BLOCK,
      freeSectors,
      usedSectors: usedSectors + fragmentedSectors,
      fragmentedFiles,
      fragmentationPercent: Math.round((fragmentedSectors / TOTAL_SECTORS_PER_BLOCK) * 100)
    });
  };

  // Animation loop for defragmentation
  useEffect(() => {
    if (!isRunning || isPaused) return;

    const interval = setInterval(() => {
      setSectors(prevSectors => {
        const newSectors = [...prevSectors];
        
          if (phase === 'analysis') {
            // Analysis phase - scan sectors
            if (currentSector < TOTAL_SECTORS_PER_BLOCK) {
              // Mark previous sectors as analyzed and processed (green)
              if (currentSector > 0) {
                newSectors[currentSector - 1].scanning = false;
                newSectors[currentSector - 1].analyzed = true;
                newSectors[currentSector - 1].processed = true; // Keep it green
              }
              // Set current sector as scanning
              if (newSectors[currentSector]) {
                newSectors[currentSector].scanning = true;
              }
              setCurrentSector(prev => prev + 1);
              
              // Calculate progress across all blocks
              const totalSectorsAcrossAllBlocks = totalBlocks * TOTAL_SECTORS_PER_BLOCK;
              const completedSectors = currentBlock * TOTAL_SECTORS_PER_BLOCK + currentSector;
              setProgress((completedSectors / totalSectorsAcrossAllBlocks) * 100);
            } else {
              // Current block complete, move to next block or finish
              if (currentBlock + 1 < totalBlocks) {
                // Move to next block
                setCurrentBlock(prev => prev + 1);
                setCurrentSector(0);
                // Don't set progress here, let the next iteration handle it
              } else {
                // All blocks complete
                setPhase('complete');
                setProgress(100);
                setIsRunning(false);
                newSectors.forEach(s => { 
                  s.scanning = false; 
                  s.analyzed = true;
                  s.processed = true; // All sectors processed and green
                });
                calculateStats(newSectors);
                
                // Save final state
                const analyzedSectorIds = newSectors.filter(s => s.analyzed).map(s => s.id);
                const processedSectorIds = newSectors.filter(s => s.processed).map(s => s.id);
                
                localStorage.setItem('diskDefragState', JSON.stringify({
                  analyzedSectors: analyzedSectorIds,
                  processedSectors: processedSectorIds,
                  lastScanTime: Date.now(),
                  phase: 'complete',
                  currentSector: TOTAL_SECTORS_PER_BLOCK,
                  currentBlock: totalBlocks - 1,
                  progress: 100
                }));
              }
            }
        } else if (phase === 'defrag') {
          // Defragmentation phase - move fragmented sectors
          const fragmentedSectors = newSectors.filter(s => s.type === 'fragmented');
          const freeSectors = newSectors.filter(s => s.type === 'free');
          
          if (fragmentedSectors.length > 0 && freeSectors.length > 0) {
            const fragmentedIndex = newSectors.findIndex(s => s.type === 'fragmented');
            const freeIndex = newSectors.findIndex(s => s.type === 'free');
            
            if (fragmentedIndex !== -1 && freeIndex !== -1) {
              // Animate movement
              newSectors[fragmentedIndex].moving = true;
              newSectors[freeIndex].moving = true;
              
              setTimeout(() => {
                setSectors(prevSectors => {
                  const updatedSectors = [...prevSectors];
                  // Move fragmented sector to free space
                  updatedSectors[freeIndex] = {
                    ...updatedSectors[fragmentedIndex],
                    id: freeIndex,
                    type: 'used',
                    moving: false
                  };
                  updatedSectors[fragmentedIndex] = {
                    id: fragmentedIndex,
                    type: 'free',
                    moving: false
                  };
                  return updatedSectors;
                });
              }, 200);
            }
            
            const defragProgress = 50 + ((1 - fragmentedSectors.length / stats.fragmentedFiles) * 50);
            setProgress(defragProgress);
          } else {
            // Defragmentation complete - save scan state
            setPhase('complete');
            setProgress(100);
            setIsRunning(false);
            calculateStats(newSectors);
            
            // Save scan state to localStorage
            const analyzedSectorIds = newSectors
              .filter(s => s.analyzed)
              .map(s => s.id);
            
            localStorage.setItem('diskDefragState', JSON.stringify({
              analyzedSectors: analyzedSectorIds,
              lastScanTime: Date.now()
            }));
          }
        }
        
        return newSectors;
      });
    }, 50);

    return () => clearInterval(interval);
  }, [isRunning, isPaused, phase, currentSector, currentBlock, totalBlocks, stats.fragmentedFiles]);

  const startDefrag = () => {
    setIsRunning(true);
    setIsPaused(false);
    setPhase('analysis');
    setCurrentSector(0);
    setProgress(0);
  };

  const pauseDefrag = () => {
    setIsPaused(!isPaused);
  };

  const stopDefrag = () => {
    // Save current state before stopping
    const analyzedSectorIds = sectors.filter(s => s.analyzed).map(s => s.id);
    const processedSectorIds = sectors.filter(s => s.processed).map(s => s.id);
    
    localStorage.setItem('diskDefragState', JSON.stringify({
      analyzedSectors: analyzedSectorIds,
      processedSectors: processedSectorIds,
      lastScanTime: Date.now(),
      phase,
      currentSector,
      currentBlock,
      progress
    }));
    
    setIsRunning(false);
    setIsPaused(false);
    setPhase('idle');
    setCurrentSector(0);
    setProgress(0);
    setSectors(prev => prev.map(s => ({ ...s, scanning: false, moving: false })));
  };

  const resetDisk = () => {
    stopDefrag();
    initializeDisk();
    setCurrentBlock(0);
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
    stopDefrag(); // Stop any running defrag when switching modes
  };

  useEffect(() => {
    calculateVFSBlocks();
  }, [calculateVFSBlocks]);
  
  useEffect(() => {
    initializeDisk();
  }, [initializeDisk, isFullscreen, currentBlock]); // Re-initialize when block changes

  const getSectorColor = (sector: DiskSector) => {
    if (sector.scanning) return 'bg-yellow-400 animate-pulse border border-yellow-600';
    if (sector.moving) return 'bg-blue-400 animate-bounce';
    if (sector.processed) return 'bg-emerald-500'; // Processed sectors are bright emerald green
    if (sector.analyzed && sector.type === 'free') return 'bg-cyan-600'; // Analyzed free space
    if (sector.analyzed && sector.type === 'used') return 'bg-lime-500'; // Analyzed used space
    
    switch (sector.type) {
      case 'free': return 'bg-blue-900';
      case 'used': return 'bg-green-600'; // Regular used space is darker green
      case 'fragmented': return 'bg-red-500';
      case 'system': return 'bg-purple-600';
      case 'bad': return 'bg-gray-800';
      default: return 'bg-gray-600';
    }
  };

  if (isFullscreen) {
    return (
      <div className="fixed inset-0 bg-black z-50 flex flex-col">
        {/* Fullscreen Header */}
        <div className="bg-gray-900 text-white p-4 flex items-center justify-between">
          <h1 className="text-2xl font-bold">Disk Defragmenter - Full Screen</h1>
          <div className="flex gap-2">
            <Button
              onClick={startDefrag}
              disabled={isRunning && !isPaused}
              variant="default"
              size="sm"
            >
              <Play className="w-4 h-4 mr-1" />
              Start
            </Button>
            <Button
              onClick={pauseDefrag}
              disabled={!isRunning}
              variant="outline"
              size="sm"
            >
              <Pause className="w-4 h-4 mr-1" />
              {isPaused ? 'Resume' : 'Pause'}
            </Button>
            <Button
              onClick={stopDefrag}
              disabled={!isRunning}
              variant="outline"
              size="sm"
            >
              <Square className="w-4 h-4 mr-1" />
              Stop
            </Button>
            <Button
              onClick={resetDisk}
              variant="outline"
              size="sm"
            >
              <RotateCcw className="w-4 h-4 mr-1" />
              Reset
            </Button>
            <Button
              onClick={toggleFullscreen}
              variant="outline"
              size="sm"
            >
              <Minimize className="w-4 h-4 mr-1" />
              Exit Fullscreen
            </Button>
          </div>
        </div>

        {/* Fullscreen Content */}
        <div className="flex-1 flex">
          {/* Large Disk Visualization */}
          <div className="flex-1 p-8 flex items-center justify-center">
            <div className="max-w-6xl w-full">
              <div className="mb-4 text-white text-center">
                <h2 className="text-xl mb-2">Block {currentBlock + 1} of {totalBlocks} ({GRID_SIZE}x{GRID_SIZE} = {TOTAL_SECTORS_PER_BLOCK.toLocaleString()} sectors)</h2>
                <div className="text-sm mb-2">VFS Size: {(vfsSize / 1024 / 1024).toFixed(2)} MB</div>
                <div className="flex justify-center items-center gap-4 text-sm">
                  <span>Status: <span className="font-bold">
                    {phase === 'idle' && 'Ready'}
                    {phase === 'analysis' && 'Analyzing...'}
                    {phase === 'defrag' && 'Defragmenting...'}
                    {phase === 'complete' && 'Complete'}
                  </span></span>
                  <span>Progress: <span className="font-bold">{Math.round(progress)}%</span></span>
                  {phase === 'analysis' && <span>Sector: {currentSector}/{TOTAL_SECTORS_PER_BLOCK}</span>}
                </div>
              </div>
              <div 
                className="grid gap-[1px] p-6 bg-gray-800 rounded border-2 border-gray-600 mx-auto"
                style={{ 
                  gridTemplateColumns: `repeat(${GRID_SIZE}, 1fr)`,
                  maxWidth: '800px',
                  aspectRatio: '1'
                }}
              >
                {sectors.map((sector) => (
                  <div
                    key={sector.id}
                    className={`aspect-square ${getSectorColor(sector)} transition-colors duration-200`}
                    title={`Sector ${sector.id}: ${sector.type}${sector.fileId ? ` (File ${sector.fileId})` : ''}`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar Stats */}
          <div className="w-80 bg-gray-900 text-white p-4 space-y-4 overflow-y-auto">
            <div className="space-y-2">
              <h3 className="text-lg font-bold">Statistics</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Total sectors:</span>
                  <span className="font-mono">{stats.totalSectors.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span>Used sectors:</span>
                  <span className="font-mono">{stats.usedSectors.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span>Free sectors:</span>
                  <span className="font-mono">{stats.freeSectors.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span>Fragmented files:</span>
                  <span className="font-mono text-red-400">{stats.fragmentedFiles}</span>
                </div>
                <div className="flex justify-between">
                  <span>Fragmentation:</span>
                  <span className="font-mono text-red-400">{stats.fragmentationPercent}%</span>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <h3 className="text-lg font-bold">Legend</h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-blue-900 border border-gray-600"></div>
                  <span>Free space</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-green-600 border border-gray-600"></div>
                  <span>Used space</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-red-500 border border-gray-600"></div>
                  <span>Fragmented files</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-purple-600 border border-gray-600"></div>
                  <span>System files</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-gray-800 border border-gray-600"></div>
                  <span>Bad sectors</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-cyan-600 border border-gray-600"></div>
                  <span>Analyzed free</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-lime-500 border border-gray-600"></div>
                  <span>Analyzed used</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-yellow-400 border border-gray-600 animate-pulse"></div>
                  <span>Scanning</span>
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-700">
              <div className="w-full bg-gray-700 rounded-full h-2 mb-2">
                <div 
                  className="bg-blue-500 h-2 rounded-full transition-all duration-300" 
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              <div className="text-center text-sm text-gray-300">
                {Math.round(progress)}% Complete
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4 bg-background min-h-full">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-foreground">Disk Defragmenter</h1>
        <div className="flex gap-2">
          <Button
            onClick={startDefrag}
            disabled={isRunning && !isPaused}
            variant="default"
            size="sm"
          >
            <Play className="w-4 h-4 mr-1" />
            Start
          </Button>
          <Button
            onClick={pauseDefrag}
            disabled={!isRunning}
            variant="outline"
            size="sm"
          >
            <Pause className="w-4 h-4 mr-1" />
            {isPaused ? 'Resume' : 'Pause'}
          </Button>
          <Button
            onClick={stopDefrag}
            disabled={!isRunning}
            variant="outline"
            size="sm"
          >
            <Square className="w-4 h-4 mr-1" />
            Stop
          </Button>
          <Button
            onClick={resetDisk}
            variant="outline"
            size="sm"
          >
            <RotateCcw className="w-4 h-4 mr-1" />
            Reset
          </Button>
          <Button
            onClick={toggleFullscreen}
            variant="outline"
            size="sm"
          >
            <Maximize className="w-4 h-4 mr-1" />
            Fullscreen
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Disk Visualization */}
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Block {currentBlock + 1} of {totalBlocks} Analysis</CardTitle>
            </CardHeader>
            <CardContent>
              <div 
                className="grid gap-0.5 p-4 bg-muted rounded border-2 border-border"
                style={{ gridTemplateColumns: `repeat(${GRID_SIZE}, 1fr)` }}
              >
                {sectors.map((sector) => (
                  <div
                    key={sector.id}
                    className={`w-1.5 h-1.5 ${getSectorColor(sector)} transition-colors duration-200`}
                    title={`Sector ${sector.id}: ${sector.type}${sector.fileId ? ` (File ${sector.fileId})` : ''}${sector.processed ? ' (Processed)' : ''}`}
                  />
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Legend - Moved under analysis and spread horizontally */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Legend</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-blue-900 border"></div>
                  <span>Free space</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-green-600 border"></div>
                  <span>Used space</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-red-500 border"></div>
                  <span>Fragmented files</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-purple-600 border"></div>
                  <span>System files</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-emerald-500 border"></div>
                  <span>Processed</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-yellow-400 border animate-pulse"></div>
                  <span>Scanning</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Controls and Stats */}
        <div className="space-y-4">
          {/* Progress */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Progress</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Status:</span>
                  <span className="font-medium">
                    {phase === 'idle' && 'Ready'}
                    {phase === 'analysis' && 'Analyzing...'}
                    {phase === 'defrag' && 'Defragmenting...'}
                    {phase === 'complete' && 'Complete'}
                  </span>
                </div>
                <Progress value={progress} className="h-2" />
                <div className="text-xs text-gray-500 mt-1">
                  {Math.round(progress)}% complete
                </div>
              </div>
              
              <div className="text-sm min-h-[2.5rem]">
                {phase === 'analysis' ? (
                  <>
                    <div>Block {currentBlock + 1} of {totalBlocks}</div>
                    <div>Analyzing sector {currentSector} of {TOTAL_SECTORS_PER_BLOCK}</div>
                  </>
                ) : (
                  <>
                    <div>Ready to scan {totalBlocks} blocks</div>
                    <div>Each block contains {TOTAL_SECTORS_PER_BLOCK} sectors</div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Statistics */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Disk Statistics</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>VFS Size:</span>
                <span className="font-mono">{(vfsSize / 1024 / 1024).toFixed(2)} MB</span>
              </div>
              <div className="flex justify-between">
                <span>Total blocks:</span>
                <span className="font-mono">{totalBlocks}</span>
              </div>
              <div className="flex justify-between">
                <span>Current block:</span>
                <span className="font-mono">{currentBlock + 1}</span>
              </div>
              <div className="flex justify-between">
                <span>Sectors per block:</span>
                <span className="font-mono">{TOTAL_SECTORS_PER_BLOCK.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span>Used sectors:</span>
                <span className="font-mono">{stats.usedSectors.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span>Free sectors:</span>
                <span className="font-mono">{stats.freeSectors.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span>Fragmented files:</span>
                <span className="font-mono text-red-600">{stats.fragmentedFiles}</span>
              </div>
              <div className="flex justify-between">
                <span>Fragmentation:</span>
                <span className="font-mono text-red-600">{stats.fragmentationPercent}%</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};